"use client";
/**
 * SANATAN DECODE — v12 "EVIDENCE". 21s @ 30fps, 1280x720.
 *
 * Same audio, same word map, same beat windows, same motion laws as v10/v11.
 * The change is a REGISTER SPLIT borrowed from the reference repo's C-roll idea (a real
 * photograph cut out and anchored inside the collage):
 *
 *     FLAT ILLUSTRATION carries the CLAIM.        PHOTOGRAPH carries the PROOF.
 *
 * Every beat now lands one photoreal sticker — a real palm-leaf manuscript, a copper-plate
 * land grant, cloth-bound genealogy ledgers, a present-day man — cut from a shared studio
 * plate so the whole set is lit identically. Each gets a cream keyline and a hard cast
 * shadow, which is what makes a photograph read as PASTED rather than composited, and is
 * also the LAW 5 separation: the sticker's own white border stops it merging into the ground.
 *
 * Assets: two 3840x1280 medium plates (~$0.085 total) split into 9 cutouts by split_sheet.py.
 * Retained: on twos, swing physics, object permanence, one accent, anchor-first, the walk.
 */
import React from "react";
import {
  AbsoluteFill, Audio, Img, staticFile,
  useCurrentFrame, useVideoConfig, interpolate, spring, Easing, random,
} from "remotion";
import { loadFont as loadDeva } from "@remotion/google-fonts/NotoSansDevanagari";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";

const { fontFamily: deva } = loadDeva("normal", { weights: ["400", "600", "700", "800", "900"], subsets: ["devanagari", "latin"] });
const { fontFamily: inter } = loadInter("normal", { weights: ["500", "700", "800"], subsets: ["latin"] });

const INK = "#22304F";
const GOLD = "#BE9433";
const MARIGOLD = "#D98324";
const KUMKUM = "#C0392B";
const MUTED_C = "#6A6250";

const FPS = 30;
const f = (s: number) => s * FPS;
const useStep = () => Math.floor(useCurrentFrame() / 2) * 2;

const W: Record<number, [number, number, string]> = {
  0: [0.0, 0.15, "आप"], 1: [0.21, 0.35, "जिस"], 2: [0.41, 0.71, "गोत्र"], 3: [0.74, 0.82, "का"],
  4: [0.87, 1.06, "नाम"], 5: [1.13, 1.36, "लेते"], 6: [1.42, 1.64, "हैं"],
  8: [2.03, 2.16, "वो"], 9: [2.26, 2.47, "आपका"], 10: [2.58, 3.01, "सरनेम"], 11: [3.05, 3.24, "नहीं"],
  12: [3.32, 3.6, "है।"],
  15: [4.35, 4.7, "ऋषि"],
  26: [8.2, 8.42, "एक"], 27: [8.45, 8.52, "ही"], 28: [8.63, 9.17, "रक्त-रेखा"],
  34: [11.61, 12.03, "शास्त्र"],
  38: [13.41, 14.03, "सगोत्र"], 39: [14.41, 14.76, "विवाह"], 40: [14.86, 15.27, "नहीं।"],
  45: [17.43, 17.86, "विज्ञान"],
};

const useKeys = (keys: [number, number][]) => {
  const frame = useStep();
  return interpolate(frame, keys.map((k) => f(k[0])), keys.map((k) => k[1]), {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic),
  });
};
const useSpringAt = (at: number, damping = 9, stiffness = 110, mass = 1.1) => {
  const frame = useStep();
  const { fps } = useVideoConfig();
  return spring({ frame: frame - f(at), fps, config: { damping, stiffness, mass } });
};

/* ══ THE GROUND — one panorama, walked (unchanged) ═════════════ */
const PANO_W = 3840, PANO_H = 1280, DISPLAY_H = 1240;
const DISPLAY_W = (PANO_W / PANO_H) * DISPLAY_H;
const TRAVEL = DISPLAY_W - 1280;
const WALK: [number, number][] = [
  [0.00, 0.0000], [3.60, 0.0492], [4.15, 0.4180], [5.45, 0.3443], [6.05, 0.8156],
  [10.25, 0.4795], [10.85, 0.8893], [15.45, 0.5205], [16.05, 1.0000], [21.20, 0.8770],
];
const walkAt = (fr: number) =>
  interpolate(fr, WALK.map((k) => f(k[0])), WALK.map((k) => k[1]), {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic),
  });

const Pano: React.FC = () => {
  const frame = useStep();
  const x = walkAt(frame);
  const vx = (Math.abs(x - walkAt(Math.max(0, frame - 2))) * TRAVEL) / 2;
  const whip = Math.min(20, vx * 0.16);
  const y = Math.sin(frame / 78) * 7;
  return (
    <AbsoluteFill style={{ backgroundColor: "#F2E6CC", overflow: "hidden" }}>
      <Img src={staticFile("sanatan/pano/gurukul_pano.png")} style={{
        position: "absolute", height: DISPLAY_H, width: DISPLAY_W,
        left: -x * TRAVEL, top: -300 + y, maxWidth: "none",
        filter: `blur(${(4.2 + whip).toFixed(2)}px) saturate(0.62) brightness(1.12) contrast(0.84)`,
      }} />
      <AbsoluteFill style={{ backgroundColor: "rgba(249,240,222,0.32)" }} />
      <AbsoluteFill style={{ background: "radial-gradient(ellipse 84% 78% at 50% 46%, rgba(255,252,242,0.32) 0%, rgba(198,158,100,0.32) 100%)" }} />
    </AbsoluteFill>
  );
};

const Grain: React.FC = () => {
  const frame = useStep();
  return (
    <AbsoluteFill style={{ pointerEvents: "none", opacity: 0.13, mixBlendMode: "multiply" }}>
      <svg width="100%" height="100%">
        <filter id="gr12"><feTurbulence type="fractalNoise" baseFrequency="0.92" numOctaves="3" seed={frame % 5} /></filter>
        <rect width="100%" height="100%" filter="url(#gr12)" />
      </svg>
    </AbsoluteFill>
  );
};

const Petals: React.FC = () => {
  const frame = useStep();
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      {Array.from({ length: 14 }, (_, i) => {
        const seed = `v12${i}`;
        const x = random(seed) * 1280;
        const sp = 0.4 + random(seed + "s") * 0.7;
        const y = ((random(seed + "y") * 800 + frame * sp) % 830) - 60;
        const sway = Math.sin((frame + i * 40) / 34) * 30;
        const r = 3 + random(seed + "r") * 4.5;
        return <div key={i} style={{
          position: "absolute", left: x + sway, top: y, width: r * 2, height: r * 1.25,
          borderRadius: "50%", background: MARIGOLD, opacity: 0.22,
          transform: `rotate(${frame * 1.4 + i * 30}deg)`,
        }} />;
      })}
    </AbsoluteFill>
  );
};

/* ══ THE STICKER ═══════════════════════════════════════════════
   A photograph reads as PASTED, not composited, because of two things: a cream keyline that
   follows its alpha, and a hard offset shadow. The keyline is faked by stacking a white
   silhouette of the same PNG slightly larger behind it — no extra assets, no SVG filters. */
const Sticker: React.FC<{ src: string; h?: number; w?: number; grade?: string; ring?: number }> =
  ({ src, h, w, grade = "saturate(0.86) contrast(1.06) brightness(1.03) sepia(0.14)", ring = 1.035 }) => (
    <div style={{ position: "relative", display: "inline-block", filter: "drop-shadow(0 20px 20px rgba(72,46,16,0.38)) drop-shadow(0 3px 3px rgba(72,46,16,0.34))" }}>
      {/* cream keyline — same art, flattened to a solid silhouette, scaled up behind */}
      <Img src={staticFile(src)} style={{
        position: "absolute", left: "50%", top: "50%",
        ...(h ? { height: h } : {}), ...(w ? { width: w } : {}),
        transform: `translate(-50%,-50%) scale(${ring})`,
        filter: "brightness(0) invert(1) drop-shadow(0 0 0 #FFF8E8)",
      }} />
      <Img src={staticFile(src)} style={{
        position: "relative", display: "block",
        ...(h ? { height: h } : {}), ...(w ? { width: w } : {}),
        filter: grade,
      }} />
    </div>
  );

/* ══ SWING ═════════════════════════════════════════════════════ */
type SwingCfg = { inAt: number; outAt: number; from?: "left" | "right" | "top"; amp?: number; travel?: number };
const useSwing = ({ inAt, outAt, from = "left", amp = 17, travel = 240 }: SwingCfg) => {
  const frame = useStep();
  const sIn = useSpringAt(inAt, 8, 95, 1.15);
  const sOut = useSpringAt(outAt, 200, 75, 0.5);
  const sign = from === "right" ? -1 : 1;
  const pend = Math.sin((frame - f(inAt)) / 34) * 1.4 * sIn * (1 - sOut);
  const dx = from === "top" ? 0 : travel * sign * (1 - sIn) - travel * 1.5 * sign * sOut;
  const dy = from === "top" ? -travel * (1 - sIn) + travel * 2 * sOut : 70 * (1 - sIn) + 190 * sOut;
  return {
    transform: `translate(${dx}px, ${dy}px) rotate(${amp * sign * (1 - sIn) + pend - 30 * sign * sOut}deg) scale(${0.9 + 0.1 * sIn - 0.12 * sOut})`,
    transformOrigin: "50% -28%",
    opacity: Math.min(1, sIn * 1.6) * (1 - Math.min(1, sOut * 1.4)),
  } as React.CSSProperties;
};

/** An illustrated prop — the CLAIM register. Unchanged from v10. */
const Prop: React.FC<{ src: string; h?: number; w?: number; left: number; top: number } & SwingCfg> =
  ({ src, h, w, left, top, ...cfg }) => {
    const style = useSwing(cfg);
    const frame = useStep();
    if (frame < f(cfg.inAt) - 4 || frame > f(cfg.outAt) + 30) return null;
    return (
      <div style={{ position: "absolute", left, top, ...style }}>
        <Img src={staticFile(src)} style={{ ...(h ? { height: h } : {}), ...(w ? { width: w } : {}), filter: "drop-shadow(0 24px 26px rgba(72,46,16,0.40)) drop-shadow(0 2px 3px rgba(72,46,16,0.30))" }} />
      </div>
    );
  };

/** A photographic sticker — the PROOF register. Lands harder and heavier than a prop:
    stiffer spring, no pendulum idle, a settling THUMP. Evidence does not float. */
const Evidence: React.FC<{
  src: string; h?: number; w?: number; left: number; top: number; tilt?: number; label?: string;
} & SwingCfg> = ({ src, h, w, left, top, tilt = 0, label, ...cfg }) => {
  const frame = useStep();
  const sIn = useSpringAt(cfg.inAt, 10, 190, 1.25);
  const sOut = useSpringAt(cfg.outAt, 200, 75, 0.5);
  // ⛔ every hook BEFORE the early return — an unmount-gate placed above a hook is a
  // "rendered fewer hooks than expected" crash, not a skipped frame.
  const lab = useSpringAt(cfg.inAt + 0.45, 11, 170, 0.8);
  if (frame < f(cfg.inAt) - 4 || frame > f(cfg.outAt) + 30) return null;
  const sign = cfg.from === "right" ? -1 : 1;
  const drop = 240 * (1 - sIn);
  return (
    <div style={{
      position: "absolute", left, top,
      transform: `translate(${sign * 60 * (1 - sIn)}px, ${-drop + 200 * sOut}px) rotate(${tilt + 9 * sign * (1 - sIn) - 24 * sign * sOut}deg) scale(${0.94 + 0.06 * sIn - 0.1 * sOut})`,
      transformOrigin: "50% -14%",
      opacity: Math.min(1, sIn * 2.4) * (1 - Math.min(1, sOut * 1.4)),
    }}>
      <Sticker src={src} h={h} w={w} />
      {label && (
        <div style={{
          position: "absolute", left: "50%", bottom: -30, transform: `translateX(-50%) translateY(${(1 - lab) * 14}px)`,
          opacity: Math.min(1, lab * 1.6),
          // ⛔ these labels are DEVANAGARI — Inter has no Devanagari glyphs and renders □□□.
          // And letterSpacing shears the shirorekha, so the safe control is wordSpacing.
          fontFamily: `${deva}, ${inter}, sans-serif`, fontSize: 15, fontWeight: 700, wordSpacing: 3, whiteSpace: "nowrap",
          color: "#FFF6E4", background: INK, padding: "4px 12px", borderRadius: 2,
        }}>{label}</div>
      )}
    </div>
  );
};

/* ══ WORDS ═════════════════════════════════════════════════════ */
const Word: React.FC<{
  idx: number; size: number; color?: string; weight?: number;
  strike?: boolean; underline?: boolean; outAt?: number;
}> = ({ idx, size, color = INK, weight = 800, strike, underline, outAt }) => {
  const [start] = W[idx];
  const frame = useStep();
  const { fps } = useVideoConfig();
  const sIn = spring({ frame: frame - f(start), fps, config: { damping: 9, stiffness: 150, mass: 0.8 } });
  const sOut = outAt ? spring({ frame: frame - f(outAt), fps, config: { damping: 200, stiffness: 80, mass: 0.5 } }) : 0;
  const grow = interpolate(frame, [f(start) + 2, f(start) + 12], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  return (
    <span style={{
      position: "relative", display: "inline-block",
      fontFamily: deva, fontWeight: weight, fontSize: size, lineHeight: 1.38, color,
      textShadow: "0 0 26px rgba(255,251,240,0.98), 0 0 10px rgba(255,251,240,0.95), 0 2px 2px rgba(255,251,240,0.8)",
      opacity: Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4)),
      transform: `translateY(${(1 - sIn) * 34}px) rotate(${9 * (1 - sIn) - 14 * sOut}deg) scale(${0.86 + sIn * 0.14})`,
      transformOrigin: "50% 140%",
    }}>
      {W[idx][2]}
      {strike && <span style={{ position: "absolute", left: -4, top: "53%", height: Math.max(4, size * 0.075), width: `calc((100% + 8px) * ${grow})`, background: KUMKUM, borderRadius: 4 }} />}
      {underline && <span style={{ position: "absolute", left: 0, bottom: -6, height: 4, width: `calc(100% * ${grow})`, background: GOLD, borderRadius: 3, opacity: 0.8 }} />}
    </span>
  );
};
const Row: React.FC<{ children: React.ReactNode; gap?: number }> = ({ children, gap = 16 }) => (
  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: `0 ${gap}px` }}>{children}</div>
);

const Disc: React.FC<{ at: number; out: number; x: number; y: number; r: number; color: string; op?: number }> =
  ({ at, out, x, y, r, color, op = 0.14 }) => {
    const frame = useStep();
    const s = useSpringAt(at, 14, 55, 1.2);
    const o = useSpringAt(out, 200, 60, 0.7);
    const drift = interpolate(frame - f(at), [0, 200], [0, 46], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
    if (s <= 0.001 || frame > f(out) + 30) return null;
    return <div style={{
      position: "absolute", left: x - r, top: y - r, width: r * 2, height: r * 2, borderRadius: "50%",
      background: color, opacity: op * s * (1 - o),
      transform: `translateX(${(1 - s) * -260 + drift + o * 320}px) scale(${0.7 + s * 0.3})`,
    }} />;
  };

/* ══ PERSISTENT OBJECTS ════════════════════════════════════════
   v12 swaps the illustrated rishi for the PHOTOGRAPHED sage, because this beat is the one
   the whole film rests on — the claim that you carry one man's name. It is still ONE
   instance travelling and shrinking, never re-introduced (LAW 3).                          */
const PersistentSage: React.FC = () => {
  const frame = useStep();
  const sIn = useSpringAt(3.9, 9, 120);
  const sOut = useSpringAt(10.2, 200, 70, 0.5);
  const left = useKeys([[3.9, 471], [5.6, 471], [6.3, 158], [10.2, 158]]);
  const top = useKeys([[3.9, 168], [5.6, 168], [6.3, 236], [10.2, 236]]);
  const h = useKeys([[3.9, 358], [5.6, 358], [6.3, 300], [10.2, 300]]);
  if (frame < f(3.7) || frame > f(10.9)) return null;
  const pend = Math.sin((frame - f(3.9)) / 40) * 0.8 * sIn;
  return (
    <div style={{
      position: "absolute", left, top,
      transform: `translate(${-120 * (1 - sIn)}px, ${-220 * (1 - sIn) + 200 * sOut}px) rotate(${7 * (1 - sIn) + pend - 20 * sOut}deg)`,
      transformOrigin: "50% -20%",
      opacity: Math.min(1, sIn * 2.2) * (1 - Math.min(1, sOut * 1.4)),
    }}>
      <Sticker src="sanatan/photo/ph_sage.png" h={h} />
    </div>
  );
};

const PersistentThread: React.FC = () => {
  const frame = useStep();
  const Y = 396, X0 = 372, X1 = 928;
  const CX = 448, PY = 232, CY = 388;
  const draw = useKeys([[6.6, 0], [7.7, 1]]);
  const collapse = useKeys([[10.0, 0], [10.5, 1]]);
  const reopen = useKeys([[16.6, 0], [17.15, 1]]);
  const sway = Math.sin(frame / 21) * 9 * draw * (1 - collapse);
  if (frame < f(6.5)) return null;
  const dotX = interpolate(collapse, [0, 1], [(X0 + X1) / 2, 640]);
  const dotY = interpolate(collapse, [0, 1], [Y - 26, 300]);
  const parkX = interpolate(reopen, [0, 1], [dotX, CX]);
  const parkY = interpolate(reopen, [0, 1], [dotY, CY]);
  const lineEnd = X0 + (X1 - X0) * draw;
  const vis = 1 - collapse;
  return (
    <svg width={1280} height={720} style={{ position: "absolute", pointerEvents: "none" }}>
      <g opacity={vis}>
        <path d={`M${X0} ${Y} Q ${(X0 + X1) / 2} ${Y - 26 - sway} ${lineEnd} ${Y - (26 + sway) * Math.sin(Math.PI * draw)}`}
          fill="none" stroke={MARIGOLD} strokeWidth={4} strokeLinecap="round" />
        {[0.2, 0.4, 0.6, 0.8].map((t, i) => draw > t ? (
          <circle key={i} cx={X0 + (X1 - X0) * t} cy={Y - (26 + sway) * Math.sin(Math.PI * t)} r={7} fill="#FBF3E2" stroke={GOLD} strokeWidth={3} />
        ) : null)}
      </g>
      <circle cx={parkX} cy={parkY} r={7 * collapse * (1 - reopen) + 4 * reopen} fill={MARIGOLD} opacity={collapse} />
      <g opacity={reopen}>
        <line x1={CX} y1={CY} x2={CX - 110 * reopen} y2={CY - (CY - PY - 38) * reopen} stroke={MARIGOLD} strokeWidth={4} strokeLinecap="round" />
        <line x1={CX} y1={CY} x2={CX + 110 * reopen} y2={CY - (CY - PY - 38) * reopen} stroke={MARIGOLD} strokeWidth={4} strokeLinecap="round" />
      </g>
    </svg>
  );
};

const Beat: React.FC<{ from: number; to: number; children: React.ReactNode }> = ({ from, to, children }) => {
  const frame = useStep();
  if (frame < f(from - 0.55) || frame >= f(to + 0.7)) return null;
  return <AbsoluteFill>{children}</AbsoluteFill>;
};

const CAM: [number, number, number, number][] = [
  [0, 1.09, 0, 26], [1.4, 1.0, 0, 0], [3.92, 1.06, 22, -12], [5.82, 1.0, -18, 8],
  [10.58, 1.05, 12, -10], [15.76, 1.0, -14, 6], [21.1, 1.08, 8, -8],
];
const useCamera = () => {
  const frame = useStep();
  const t = CAM.map((c) => f(c[0]));
  const e = { extrapolateLeft: "clamp" as const, extrapolateRight: "clamp" as const, easing: Easing.inOut(Easing.quad) };
  return `translate(${interpolate(frame, t, CAM.map((c) => c[2]), e)}px, ${interpolate(frame, t, CAM.map((c) => c[3]), e)}px) scale(${interpolate(frame, t, CAM.map((c) => c[1]), e)})`;
};

/* ══ BEATS ═════════════════════════════════════════════════════ */
/** ANCHOR FIRST — and the anchor is now a REAL manuscript, on screen at frame 0, large. */
const BeatA: React.FC = () => (
  <>
    <Disc at={0.0} out={3.6} x={880} y={300} r={250} color={MARIGOLD} />
    <Evidence src="sanatan/photo/ph_manuscript.png" w={640} left={332} top={392}
      inAt={-0.4} outAt={3.55} from="right" tilt={-2.2} label="ताड़पत्र · पीढ़ी-दर-पीढ़ी" />
    <div style={{ position: "absolute", left: 0, right: 0, top: 128 }}>
      <Row gap={14}>{[0, 1, 2, 3, 4, 5, 6].map((i) => <Word key={i} idx={i} size={38} color={MUTED_C} weight={600} underline={i === 2} outAt={3.6} />)}</Row>
      <div style={{ height: 16 }} />
      <Row>
        <Word idx={8} size={74} weight={900} outAt={3.62} />
        <Word idx={9} size={74} weight={900} outAt={3.64} />
        <Word idx={10} size={74} weight={900} color={KUMKUM} strike outAt={3.66} />
        <Word idx={11} size={74} weight={900} outAt={3.68} />
        <Word idx={12} size={74} weight={900} outAt={3.7} />
      </Row>
    </div>
  </>
);

const BeatB: React.FC = () => {
  const halo = useSpringAt(4.35, 11, 80);
  const gone = useSpringAt(5.5, 200, 70);
  return (
    <>
      <Disc at={3.9} out={5.5} x={640} y={300} r={280} color={GOLD} op={0.18} />
      <svg width={1280} height={720} style={{ position: "absolute", opacity: halo * 0.5 * (1 - gone) }}>
        <circle cx={612} cy={260} r={128 + halo * 34} fill="none" stroke={GOLD} strokeWidth={2} />
        <circle cx={612} cy={260} r={96 + halo * 22} fill="none" stroke={MARIGOLD} strokeWidth={1.5} />
      </svg>
      <div style={{ position: "absolute", left: 0, right: 0, top: 544, textAlign: "center" }}>
        <Row><Word idx={15} size={72} weight={900} color={KUMKUM} underline outAt={5.52} /></Row>
      </div>
    </>
  );
};

const ChainLabel: React.FC<{ text: string; at: number; x: number; y: number; size?: number; color?: string; weight?: number }> =
  ({ text, at, x, y, size = 21, color = MUTED_C, weight = 700 }) => {
    const sIn = useSpringAt(at, 9, 160, 0.8);
    const sOut = useSpringAt(10.2, 200, 80, 0.5);
    return (
      <div style={{
        position: "absolute", left: x - 90, top: y, width: 180, textAlign: "center",
        fontFamily: deva, fontSize: size, fontWeight: weight, color,
        textShadow: "0 0 22px rgba(255,251,240,0.98), 0 0 9px rgba(255,251,240,0.95)",
        opacity: Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4)),
        transform: `translateY(${(1 - sIn) * 18 + sOut * 60}px) rotate(${6 * (1 - sIn)}deg)`,
        transformOrigin: "50% 140%",
      }}>{text}</div>
    );
  };

const CHAIN_X0 = 372, CHAIN_X1 = 928, CHAIN_Y = 396;
const CHAIN: [string, number, number][] = [
  ["पुत्र", 0.2, 6.80], ["पौत्र", 0.4, 6.98], ["प्रपौत्र", 0.6, 7.16], ["कई पीढ़ी", 0.8, 7.36],
];

/** The chain runs from a PHOTOGRAPHED sage at the source to a PHOTOGRAPHED man today, with
    the generations in between existing only as words — the register split doing narrative
    work: the two ends are real, the middle is inherited on faith. The ledgers land as the
    one piece of evidence that the middle was ever written down. */
const BeatC: React.FC = () => (
  <>
    <Disc at={5.8} out={10.25} x={300} y={330} r={230} color={KUMKUM} op={0.1} />
    <Disc at={6.6} out={10.25} x={1000} y={330} r={230} color={INK} op={0.09} />
    <Evidence src="sanatan/photo/ph_modern_man.png" h={330} left={968} top={214}
      inAt={7.0} outAt={10.25} from="right" tilt={1.6} />
    <Evidence src="sanatan/photo/ph_ledgers.png" h={148} left={548} top={172}
      inAt={8.7} outAt={10.25} from="left" tilt={-3.4} label="वंशावली · बही" />
    <ChainLabel text="ऋषि" at={6.30} x={262} y={560} size={26} color={INK} weight={800} />
    {CHAIN.map(([t, frac, at]) => (
      <ChainLabel key={t} text={t} at={at} x={CHAIN_X0 + (CHAIN_X1 - CHAIN_X0) * frac} y={CHAIN_Y - 78} />
    ))}
    <ChainLabel text="आप" at={7.52} x={1050} y={560} size={26} color={KUMKUM} weight={800} />
    <div style={{ position: "absolute", left: 372, width: 556, top: 452, textAlign: "center" }}>
      <Row gap={14}>
        <Word idx={26} size={50} weight={800} outAt={10.2} />
        <Word idx={27} size={50} weight={800} outAt={10.22} />
        <Word idx={28} size={62} weight={900} color={KUMKUM} underline outAt={10.24} />
      </Row>
    </div>
  </>
);

/** शास्त्र stops being a caption and becomes an object: a real copper-plate grant lands as
    the rule is named, and the illustrated couple stays the claim above it. */
const BeatDE: React.FC = () => {
  const archStyle = useSwing({ inAt: 11.0, outAt: 15.45, from: "left", amp: 7, travel: 150 });
  const AW = 900, AH = 344;
  const archPath = `M0 ${AH} L0 ${AH * 0.52}
    Q ${AW * 0.06} ${AH * 0.30} ${AW * 0.20} ${AH * 0.22}
    Q ${AW * 0.34} ${AH * 0.13} ${AW * 0.42} 0
    Q ${AW * 0.5} ${AH * 0.10} ${AW * 0.58} 0
    Q ${AW * 0.66} ${AH * 0.13} ${AW * 0.80} ${AH * 0.22}
    Q ${AW * 0.94} ${AH * 0.30} ${AW} ${AH * 0.52} L ${AW} ${AH} Z`;
  return (
    <>
      <Disc at={10.6} out={15.4} x={640} y={280} r={300} color={MARIGOLD} op={0.13} />
      <Prop src="sanatan/couple.png" w={392} left={444} top={62} inAt={10.65} outAt={15.42} from="top" amp={11} travel={280} />
      <div style={{ position: "absolute", left: 190, top: 336, ...archStyle }}>
        <svg width={AW} height={AH} style={{ overflow: "visible" }}>
          <path d={archPath} fill="#FFFCF2" stroke={GOLD} strokeWidth={2.5} />
        </svg>
        <div style={{ position: "absolute", left: 0, top: 150, width: AW, textAlign: "center" }}>
          <Row gap={18}>
            <Word idx={38} size={70} weight={900} outAt={15.4} />
            <Word idx={39} size={70} weight={900} outAt={15.42} />
            <Word idx={40} size={70} weight={900} color={KUMKUM} outAt={15.44} />
          </Row>
        </div>
      </div>
      <Evidence src="sanatan/photo/ph_copperplate.png" h={196} left={86} top={392}
        inAt={11.61} outAt={15.44} from="left" tilt={-4} label="ताम्रपत्र · शास्त्र" />
    </>
  );
};

const BeatF: React.FC = () => {
  const frame = useStep();
  const CX = 448, PY = 232, CY = 388;
  const pA = useSpringAt(15.9, 9, 140);
  const pB = useSpringAt(16.2, 9, 140);
  const kid = useSpringAt(17.15, 8, 150);
  const term = useSpringAt(17.43, 10, 120);
  const bob = Math.sin(frame / 24) * 5;
  const Allele: React.FC<{ x: number; fill: string; opacity?: number }> = ({ x, fill, opacity = 1 }) => (
    <rect x={x - 7} y={-20} width={14} height={40} rx={7} fill={fill} opacity={opacity} />
  );
  const Carrier: React.FC<{ x: number; s: number; ph: number }> = ({ x, s, ph }) => (
    <g opacity={s} transform={`translate(${x},${PY + Math.sin((frame + ph) / 26) * 6}) scale(${0.8 + s * 0.2}) rotate(${8 * (1 - s)})`}>
      <circle r={44} fill="#FDF8EC" stroke={INK} strokeWidth={4} />
      <Allele x={-15} fill={INK} opacity={0.22} />
      <Allele x={15} fill={KUMKUM} />
    </g>
  );
  return (
    <>
      <Disc at={15.8} out={22} x={1010} y={330} r={270} color={INK} op={0.09} />
      {/* the science beat stays fully ILLUSTRATED — there is no photograph of a gene pool,
          and faking one would break the register the film has just spent 15s establishing. */}
      <Prop src="sanatan/dna.png" h={520} left={940} top={110} inAt={15.85} outAt={22} from="right" amp={14} />
      <svg width={1280} height={720} style={{ position: "absolute" }}>
        <Carrier x={CX - 128} s={pA} ph={0} />
        <Carrier x={CX + 128} s={pB} ph={40} />
        <g opacity={kid} transform={`translate(${CX},${CY + bob}) scale(${0.78 + kid * 0.22}) rotate(${-10 * (1 - kid)})`}>
          <circle r={46} fill={KUMKUM} opacity={0.14} />
          <circle r={46} fill="none" stroke={KUMKUM} strokeWidth={5} />
          <Allele x={-15} fill={KUMKUM} />
          <Allele x={15} fill={KUMKUM} />
        </g>
      </svg>
      <div style={{
        position: "absolute", left: 84, top: 486, width: 730, textAlign: "center",
        opacity: Math.min(1, term * 1.4),
        transform: `translateY(${(1 - term) * 34}px) rotate(${4 * (1 - term)}deg)`,
      }}>
        <div style={{ fontFamily: inter, fontSize: 42, fontWeight: 800, letterSpacing: 6, color: KUMKUM }}>CONSANGUINITY</div>
        <div style={{ height: 8 }} />
        <div style={{ fontFamily: deva, fontSize: 25, fontWeight: 700, color: INK, opacity: 0.8 }}>विज्ञान ने इसे एक नाम दिया।</div>
      </div>
    </>
  );
};

const Furniture: React.FC = () => {
  const frame = useStep();
  const { durationInFrames } = useVideoConfig();
  const p = interpolate(frame, [0, durationInFrames], [0, 1]);
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <div style={{ position: "absolute", top: 42, left: 50, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 4, height: 18, background: MARIGOLD, borderRadius: 2 }} />
        <span style={{ fontFamily: deva, fontWeight: 700, fontSize: 17, wordSpacing: 7, color: INK, opacity: 0.6 }}>सनातन डिकोड</span>
      </div>
      <div style={{ position: "absolute", left: 0, bottom: 0, height: 3, width: `${p * 100}%`, background: MARIGOLD }} />
    </AbsoluteFill>
  );
};

export const SanatanDecodeGotraV12: React.FC = () => {
  const cam = useCamera();
  return (
    <AbsoluteFill style={{ backgroundColor: "#F2E6CC", overflow: "hidden" }}>
      <Audio src={staticFile("sanatan/gotra.mp3")} />
      <Pano />
      <AbsoluteFill style={{ transform: cam }}>
        <Beat from={0} to={3.75}><BeatA /></Beat>
        <Beat from={3.9} to={5.6}><BeatB /></Beat>
        <Beat from={5.8} to={10.4}><BeatC /></Beat>
        <Beat from={10.6} to={15.6}><BeatDE /></Beat>
        <Beat from={15.8} to={21.1}><BeatF /></Beat>
        <PersistentSage />
        <PersistentThread />
      </AbsoluteFill>
      <Petals />
      <Grain />
      <Furniture />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
